'use client'

import {
  type ReactNode,
  Children,
  createContext,
  Fragment,
  isValidElement,
  useContext,
} from 'react'

import { cn, passPropsToSingleChild } from '@/shared/lib'

import { DefaultStepIcon, StepStatus } from './step'

export type StepIcon = {
  active: ReactNode
  completed: ReactNode
  disabled: ReactNode
}

type StepperState = {
  stepIcon: StepIcon | null
}

const StepperContext = createContext<StepperState>({
  stepIcon: null,
})

type RootProps = {
  activeStep?: number
  stepIcon?: StepIcon
  children?: ReactNode
}

export const Root = ({ activeStep = 0, stepIcon, children }: RootProps) => {
  const steps = Children.toArray(children)

  const isFirstStep = (index: number) => index === 0

  const getStatus = (index: number) => {
    if (index === activeStep) return StepStatus.active
    if (index < activeStep) return StepStatus.completed

    return StepStatus.disabled
  }

  return (
    <StepperContext.Provider value={{ stepIcon: stepIcon || null }}>
      <div>
        {steps.map((step, index) => {
          const isFirstItem = isFirstStep(index)

          const stepId = isValidElement(step) ? step.key : `step-${index}`

          if (isFirstItem) {
            return passPropsToSingleChild(step, {
              status: getStatus(index),
              key: stepId,
            })
          }

          return (
            <Fragment key={stepId}>
              <div className="ml-[12.5px] h-5 border-l border-gray-200" />
              {passPropsToSingleChild(step, {
                status: getStatus(index),
              })}
            </Fragment>
          )
        })}
      </div>
    </StepperContext.Provider>
  )
}

type StepItemProps = {
  status?: StepStatus
  className?: string
  children: ReactNode
}

export const Step = ({ status, className, children }: StepItemProps) => {
  const { stepIcon } = useContext(StepperContext)

  if (!status) {
    return null
  }

  const icon = stepIcon ? stepIcon[status] : DefaultStepIcon[status]

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {icon}
      {passPropsToSingleChild(children, {
        status,
      })}
    </div>
  )
}

export const Label = ({ status, className, children }: StepItemProps) => {
  return (
    <div
      className={cn(
        'typo-body1-reading font-normal text-gray-900',
        status === StepStatus.active && 'font-semibold',
        status === StepStatus.disabled && 'text-gray-300',
        className,
      )}
    >
      {children}
    </div>
  )
}
